import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2, AlertCircle, Building2, User, LogOut } from 'lucide-react';
import { supabase } from '../lib/supabase';

const countries = [
  'Argentina',
  'Bolivia',
  'Brasil',
  'Chile',
  'Colombia',
  'Ecuador',
  'Paraguay',
  'Perú',
  'Uruguay',
  'Venezuela',
  'México',
  'Estados Unidos',
  'España',
  'Otro'
];

export const Onboarding = () => {
  const navigate = useNavigate();
  // Check URL parameters for initial step
  const urlParams = new URLSearchParams(window.location.search);
  const initialStep = urlParams.get('step') === 'role' ? 'role' : 'personal';
  
  const [step] = useState<'personal' | 'role'>(initialStep);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    pais: ''
  });

  const handleSubmitPersonal = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          pais: formData.pais
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      // Skip role selection and go directly to owner onboarding
      navigate('/owner-onboarding');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role: 'owner' | 'employee') => {
    if (role === 'owner') {
      // Bypassing access code page and going directly to owner onboarding
      // with empty access code (temporarily disabled)
      navigate('/owner-onboarding');
    } else {
      navigate('/employee-onboarding');
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/auth');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };
  if (step === 'personal') {
    return (
      <div className="min-h-screen bg-white md:bg-[#EFF0F5] flex items-center justify-center p-4">
        <div className="w-full max-w-md md:max-w-xl">
          {/* Form Card */}
          <div className="bg-white rounded-none md:rounded-xl shadow-none md:shadow-lg p-8">
            <p className="text-gray-500 text-sm md:text-base font-normal mb-2">
              Bienvenido a Atlas
            </p>
            <h2 className="text-3xl md:text-2xl font-semibold text-gray-900 mb-8">
              Vamos a conocernos ¿Cómo te llamas?
            </h2>
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <p>{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmitPersonal} className="space-y-10">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre y apellido
                </label>
                <input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full h-12 px-5 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Tu nombre"
                  required
                />
              </div>

              <div>
                <label htmlFor="pais" className="block text-sm font-medium text-gray-700 mb-1">
                  País
                </label>
                <div className="relative">
                  <select
                    id="pais"
                    value={formData.pais}
                    onChange={(e) => setFormData({ ...formData, pais: e.target.value })}
                    className="w-full h-12 px-5 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                    required
                  >
                    <option value="">Selecciona tu país</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="order-1 md:order-2 w-full md:w-auto bg-blue-500 text-white h-12 px-6 rounded-full hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Continuar'
                  )}
                </button>
                <button
                  type="button"
                  onClick={handleSignOut}
                  className="order-2 md:order-1 h-12 px-5 rounded-full text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Cerrar sesión</span>
                </button>
              </div>
            </form>

          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white md:bg-[#EFF0F5] flex items-center justify-center p-4">
      <div className="w-full max-w-md md:max-w-xl">

        {/* Role Selection Card */}
        <div className="bg-white rounded-none md:rounded-xl shadow-none md:shadow-lg p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            ¿Cuál es tu rol?
          </h2>
          <p className="text-gray-500 mb-6">
            Selecciona tu rol para continuar
          </p>

          <div className="space-y-4">
            <button 
              onClick={() => handleRoleSelect('owner')}
              className="w-full p-6 border-2 border-gray-200 rounded-full hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center gap-4"
            >
              <div className="flex-shrink-0 flex items-center justify-center w-8 h-8">
                <Building2 className="w-7 h-7 text-blue-500" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-medium text-gray-900">Dueño / Emprendedor</h3>
                <p className="text-sm text-gray-500 break-words">Tengo mi propio negocio o emprendimiento</p>
              </div>
            </button>

            <button 
              onClick={() => handleRoleSelect('employee')}
              className="w-full p-6 border-2 border-gray-200 rounded-full hover:border-blue-500 hover:bg-blue-50 transition-colors flex items-center gap-4"
            >
              <div className="flex-shrink-0 flex items-center justify-center w-8 h-8">
                <User className="w-8 h-8 text-blue-500" />
              </div>
              <div className="text-left flex-1 min-w-0">
                <h3 className="font-medium text-gray-900">Empleado</h3>
                <p className="text-sm text-gray-500 break-words">Trabajo para una empresa</p>
              </div>
            </button>
          </div>
          
          {/* Sign Out Button */}
          <div className="mt-6 text-center">
            <button
              onClick={handleSignOut}
              className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1 mx-auto"
            >
              <LogOut className="w-3 h-3" />
              <span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};